import React, { Fragment, useCallback, useMemo } from 'react';

import { ProfileData, useAuth } from 'hooks/useAuth';
import { ClosingData, SessionDetailsData } from 'hooks/useMemberGroupData';

import { formatDate } from 'libs/utils';

import { Bumper, Divider } from 'styles/shared';
import { colors, Fonts, spacing, transparencies } from 'styles/theme';

interface GroupLearningsProps {
    closings: Array<ClosingData>;
    sessionDetails: SessionDetailsData;
    memberProfiles?: Array<ProfileData>;
    shouldShowCoachVisibility?: boolean;
}

export const GroupLearnings = ({
    closings,
    sessionDetails,
    memberProfiles = [],
    shouldShowCoachVisibility = false,
}: GroupLearningsProps) => {
    const { userId } = useAuth();

    const closingsBySession = useMemo(() => {
        const closingsBySessionToReturn: Array<Array<ClosingData>> = [];
        closings.forEach((closing) => {
            const sessionClosings =
                closingsBySessionToReturn[closing.session_number ?? 0];
            if (sessionClosings == null) {
                closingsBySessionToReturn[closing.session_number ?? 0] = [
                    closing,
                ];
            } else {
                const newSessionClosings = structuredClone(sessionClosings);
                if (closing.created_by === userId) {
                    newSessionClosings.push(closing);
                } else {
                    newSessionClosings.unshift(closing);
                }
                closingsBySessionToReturn[closing.session_number ?? 0] =
                    newSessionClosings;
            }
        });
        return closingsBySessionToReturn;
    }, [closings, userId]);

    const getSessionTitle = useCallback(
        (sessionNumber: number) => {
            const sessionDetailsForClosing = sessionDetails[sessionNumber - 1];
            return sessionDetailsForClosing == null
                ? ''
                : sessionDetailsForClosing.title;
        },
        [sessionDetails]
    );

    const renderGroupLearnings = useCallback(
        (sessionClosings: Array<ClosingData>, index: number) => {
            const userClosing = sessionClosings.find(
                (closing) => closing.created_by === userId
            );
            const userClosingParsedRawData = JSON.parse(
                JSON.stringify(userClosing?.raw_data ?? {})
            );
            return (
                <>
                    {index === 0 ? null : (
                        <>
                            <Divider
                                $backgroundColor={transparencies.darkJungle20}
                            />
                            <Bumper $minHeight={spacing.xSmall} />
                        </>
                    )}
                    <Fonts.XLarge
                        $color={colors.mountainAsh}
                    >{`Session ${sessionClosings[0]?.session_number}`}</Fonts.XLarge>
                    {sessionClosings[0]?.session_date == null ? null : (
                        <Fonts.Medium $color={colors.grandGreen}>
                            {formatDate(
                                new Date(sessionClosings[0]?.session_date ?? 0)
                            )}
                        </Fonts.Medium>
                    )}
                    {sessionDetails.length > 0 ? (
                        <>
                            <Bumper $minHeight={spacing.xSmall} />
                            <Fonts.Heading7>
                                {getSessionTitle(
                                    sessionClosings[0]?.session_number ?? 0
                                )}
                            </Fonts.Heading7>
                        </>
                    ) : null}
                    {sessionClosings.map((closing) => {
                        const parsedRawData = JSON.parse(
                            JSON.stringify(closing.raw_data ?? {})
                        );
                        const memberProfile = memberProfiles.find(
                            (profile) => profile.id === closing.created_by
                        );
                        return (
                            <>
                                <Bumper
                                    $minHeight={
                                        shouldShowCoachVisibility
                                            ? spacing.xLarge
                                            : spacing.small
                                    }
                                />
                                {closing.created_by === userId ? (
                                    <>
                                        <Bumper $minHeight={spacing.xxSmall} />
                                        <Fonts.Small
                                            $isOpenSansSemiBold
                                        >{`Your learning:`}</Fonts.Small>
                                        <Bumper $minHeight={spacing.xxxSmall} />
                                    </>
                                ) : shouldShowCoachVisibility &&
                                  memberProfile != null ? (
                                    <>
                                        <Fonts.Small $isOpenSansSemiBold>
                                            {`${memberProfile.full_name}`}
                                        </Fonts.Small>
                                        <Bumper $minHeight={spacing.xxxSmall} />
                                    </>
                                ) : null}
                                <Fonts.Small>
                                    {shouldShowCoachVisibility
                                        ? `Session learning: `
                                        : ''}
                                    <Fonts.MediumQuote>
                                        {`“${parsedRawData?.learnings}”`}
                                    </Fonts.MediumQuote>
                                </Fonts.Small>
                                {shouldShowCoachVisibility &&
                                    parsedRawData.action != null &&
                                    parsedRawData.action !== '' && (
                                        <>
                                            <Bumper
                                                $minHeight={spacing.xxxSmall}
                                            />
                                            <Fonts.Small>
                                                {`They committed to `}
                                                <Fonts.MediumQuote>{`“${parsedRawData.action}”`}</Fonts.MediumQuote>
                                                {` before the next session`}
                                            </Fonts.Small>
                                        </>
                                    )}
                                {shouldShowCoachVisibility &&
                                    parsedRawData.exit != null &&
                                    parsedRawData.exit !== '' && (
                                        <>
                                            <Bumper
                                                $minHeight={spacing.xxxSmall}
                                            />
                                            <Fonts.Small>
                                                {`Their reaction to whether the session was a great use of their time was `}
                                                <Fonts.MediumQuote>{`“${parsedRawData.exit}”`}</Fonts.MediumQuote>
                                            </Fonts.Small>
                                        </>
                                    )}
                            </>
                        );
                    })}
                    {userClosingParsedRawData?.action == null ||
                    userClosingParsedRawData.action === '' ? null : (
                        <>
                            <Bumper $minHeight={spacing.xSmall} />
                            <Fonts.Small
                                $isOpenSansSemiBold
                            >{`Your “Tiny Actions”:`}</Fonts.Small>
                            <Bumper $minHeight={spacing.xxxSmall} />
                            <Fonts.MediumQuote>
                                {userClosingParsedRawData?.action}
                            </Fonts.MediumQuote>
                            <Bumper $minHeight={spacing.xSmall} />
                        </>
                    )}
                    <Bumper $minHeight={spacing.xSmall} />
                </>
            );
        },
        [
            getSessionTitle,
            memberProfiles,
            sessionDetails.length,
            shouldShowCoachVisibility,
            userId,
        ]
    );

    return closingsBySession.length === 0 ? (
        <>
            <Fonts.MediumQuote>
                {`This is where your group members will exchange learnings once your sessions begin.`}
            </Fonts.MediumQuote>
            <Bumper />
        </>
    ) : (
        <>{closingsBySession.reverse().map(renderGroupLearnings)}</>
    );
};
